import { useState } from "react";
import type { Vistoria } from "@/types/vistorias";
import { estadoFitoVistoriaConfig, riscoVistoriaConfig } from "@/config/vistorias-config";
import InspectionStatusBadge from "./InspectionStatusBadge";
import InspectionTypeBadge from "./InspectionTypeBadge";

interface InspectionTableProps {
  vistorias: Vistoria[];
  onSelect: (v: Vistoria) => void;
  onAction: (action: string, v: Vistoria) => void;
}

export default function InspectionTable({ vistorias, onSelect, onAction }: InspectionTableProps) {
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  if (vistorias.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-background-200/70 p-12 text-center">
        <div className="w-14 h-14 rounded-2xl bg-background-100 flex items-center justify-center mx-auto mb-4">
          <i className="ri-clipboard-line text-foreground-400 text-2xl"></i>
        </div>
        <h3 className="text-base font-semibold text-foreground-900 mb-1">Nenhuma vistoria encontrada</h3>
        <p className="text-sm text-foreground-500">Tente ajustar os filtros ou limpar a busca.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-background-200/70 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-background-200/70 bg-background-50/50">
              <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Código CTA</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Espécie</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Projeto</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Data</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Tipo</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Técnico</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Fito</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Risco</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Recomendação</th>
              <th className="text-center px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Fotos</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Status</th>
              <th className="text-center px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Ações</th>
            </tr>
          </thead>
          <tbody>
            {vistorias.map((v) => {
              const fitoConfig = estadoFitoVistoriaConfig[v.estadoFitossanitario] || { badge: "", label: v.estadoFitossanitario };
              const riscoConfig = riscoVistoriaConfig[v.classeRisco] || { badge: "", label: v.classeRisco };
              return (
                <tr
                  key={v.id}
                  className="border-b border-background-100 hover:bg-background-50/70 transition-colors cursor-pointer"
                  onClick={() => onSelect(v)}
                >
                  <td className="px-4 py-3">
                    <span className="font-mono text-xs text-accent-600 font-medium whitespace-nowrap">{v.codigoCta}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-foreground-800 font-medium whitespace-nowrap">{v.nomePopular}</span>
                    <span className="block text-xs text-foreground-400 italic whitespace-nowrap">{v.especie.split(" ").slice(0, 2).join(" ")}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs text-foreground-600 max-w-[140px] truncate block">{v.projeto}</span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-foreground-700 text-xs">
                    {new Date(v.data + "T00:00:00").toLocaleDateString("pt-BR")}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <InspectionTypeBadge type={v.tipo} />
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-xs text-foreground-700">{v.tecnico}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`badge ${fitoConfig.badge}`}>{fitoConfig.label}</span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`badge ${riscoConfig.badge}`}>{riscoConfig.label}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs text-foreground-700 max-w-[160px] truncate block">{v.recomendacao}</span>
                  </td>
                  <td className="px-4 py-3 text-center whitespace-nowrap">
                    <span className="text-xs text-foreground-500">{v.fotos}</span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <InspectionStatusBadge status={v.status} />
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="relative" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => setOpenMenuId(openMenuId === v.id ? null : v.id)}
                        className="w-8 h-8 rounded-lg hover:bg-background-100 flex items-center justify-center transition-colors cursor-pointer"
                        title="Ações"
                      >
                        <i className="ri-more-2-fill text-foreground-500"></i>
                      </button>
                      {openMenuId === v.id && (
                        <>
                          <div className="fixed inset-0 z-10" onClick={() => setOpenMenuId(null)}></div>
                          <div className="absolute right-0 top-full mt-1 z-20 bg-white rounded-xl border border-background-200/70 shadow-lg py-1.5 min-w-[180px]">
                            <button onClick={() => { onAction("detail", v); setOpenMenuId(null); }} className="w-full text-left px-3.5 py-2 text-xs text-foreground-700 hover:bg-background-50 flex items-center gap-2 whitespace-nowrap cursor-pointer">
                              <i className="ri-eye-line text-foreground-400"></i> Ver detalhes
                            </button>
                            <button onClick={() => { onAction("edit", v); setOpenMenuId(null); }} className="w-full text-left px-3.5 py-2 text-xs text-foreground-700 hover:bg-background-50 flex items-center gap-2 whitespace-nowrap cursor-pointer">
                              <i className="ri-edit-line text-foreground-400"></i> Editar
                            </button>
                            <button onClick={() => { onAction("validate", v); setOpenMenuId(null); }} className="w-full text-left px-3.5 py-2 text-xs text-foreground-700 hover:bg-background-50 flex items-center gap-2 whitespace-nowrap cursor-pointer">
                              <i className="ri-check-double-line text-foreground-400"></i> Validar
                            </button>
                            <button onClick={() => { onAction("fca", v); setOpenMenuId(null); }} className="w-full text-left px-3.5 py-2 text-xs text-foreground-700 hover:bg-background-50 flex items-center gap-2 whitespace-nowrap cursor-pointer">
                              <i className="ri-plant-line text-foreground-400"></i> Ver FCA
                            </button>
                            <button onClick={() => { onAction("map", v); setOpenMenuId(null); }} className="w-full text-left px-3.5 py-2 text-xs text-foreground-700 hover:bg-background-50 flex items-center gap-2 whitespace-nowrap cursor-pointer">
                              <i className="ri-map-pin-line text-foreground-400"></i> Abrir no mapa
                            </button>
                            <button onClick={() => { onAction("photos", v); setOpenMenuId(null); }} className="w-full text-left px-3.5 py-2 text-xs text-foreground-700 hover:bg-background-50 flex items-center gap-2 whitespace-nowrap cursor-pointer">
                              <i className="ri-camera-line text-foreground-400"></i> Adicionar fotos
                            </button>
                            <button onClick={() => { onAction("report", v); setOpenMenuId(null); }} className="w-full text-left px-3.5 py-2 text-xs text-foreground-700 hover:bg-background-50 flex items-center gap-2 whitespace-nowrap cursor-pointer">
                              <i className="ri-file-text-line text-foreground-400"></i> Gerar parecer
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="px-4 py-2.5 border-t border-background-100 bg-background-50/30 flex items-center justify-between">
        <span className="text-xs text-foreground-500">{vistorias.length} vistoria{vistorias.length !== 1 ? "s" : ""} encontrada{vistorias.length !== 1 ? "s" : ""}</span>
        <div className="flex items-center gap-1">
          <span className="text-xs text-foreground-500 mr-2">Página 1 de 1</span>
          <button className="w-7 h-7 rounded-md border border-background-200/70 flex items-center justify-center text-foreground-400 hover:bg-background-100 transition-colors cursor-pointer" disabled>
            <i className="ri-arrow-left-s-line text-xs"></i>
          </button>
          <button className="w-7 h-7 rounded-md border border-background-200/70 flex items-center justify-center text-foreground-400 hover:bg-background-100 transition-colors cursor-pointer" disabled>
            <i className="ri-arrow-right-s-line text-xs"></i>
          </button>
        </div>
      </div>
    </div>
  );
}